import clsx from "clsx";
import { memo, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdCategory, MdCheck, MdClose } from "react-icons/md";
import { EButtonKind } from "src/components/Button/Button.types";
import ColorPicker from "src/components/ColorPicker/ColorPicker";
import ConfirmDialog from "src/components/ConfirmDialog/ConfirmDialog";
import Icon from "src/components/Icon/Icon";
import IconSelector from "src/components/IconSelector/IconSelector";
import Modal from "src/components/Modal/Modal";
import ModalHasUnsavedChanges from "src/components/Modal/ModalHasUnsavedChanges";
import TextInput from "src/components/TextInput/TextInput";
import { ICON_SIZES } from "src/constants/icons";
import { ASK_CONFIRMATION_ON_SAVE_OR_UPDATE } from "src/helpers/constants";
import {
  objectHasChanges,
  rgbaObjectToString,
  showNotification,
} from "src/helpers/utils";
import useCache from "src/hooks/useCache";
import categoriesStore from "src/store/categoriesStore";
import useModalsStore from "src/store/modalsStore";

export interface IEditCategoryModalPayload {
  name: string;
  icon: string;
  color: any;
  background: any;
}

const EditCategoryModal: React.FC<IEditCategoryModalPayload> = ({
  name,
  icon,
  color,
  background,
}) => {
  const { t } = useTranslation();
  const { clearCache } = useCache();
  const { addCategory } = categoriesStore();
  const { setShowEditCategoryModal } = useModalsStore();
  const [showUpdateDialog, setShowUpdateDialog] = useState<boolean>(false);
  const [categoryName, setCategoryName] = useState<string>(name);
  const [textColor, setTextColor] = useState<any>(color);
  const [bgColor, setBgColor] = useState<any>(background);
  const [selectedIcon, setSelectedIcon] = useState<string>(icon);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  const hasChanges = useMemo(() => {
    return objectHasChanges({
      original: {
        name: name,
        background,
        color,
        icon,
      },
      edited: {
        name: categoryName,
        background: bgColor,
        color: textColor,
        icon: selectedIcon,
      },
    });
  }, [
    categoryName,
    bgColor,
    textColor,
    selectedIcon,
    name,
    background,
    icon,
    color,
  ]);

  const closeModal = () => {
    if (!isUpdating) {
      setShowEditCategoryModal(false, null);
    }
  };

  const updateCategory = async () => {
    setIsUpdating(true);
    await addCategory({
      onResolved: async (res: any) => {
        await clearCache();
        showNotification({
          message: t("Cambios guardados"),
          kind: "success",
        });

        setIsUpdating(false);
        setShowUpdateDialog(false);
        closeModal();
      },
      onError: (err: any) => {
        showNotification({
          message: t("Error guardando los cambios"),
          kind: "error",
        });
        setIsUpdating(false);
      },
      params: {
        category: categoryName,
        settings: {
          color: textColor,
          bg: bgColor,
          icon: selectedIcon,
        },
        originalCategory: name,
      },
    });
  };

  const hasFieldsComplete = useMemo(() => {
    return categoryName.trim() && textColor && bgColor && selectedIcon;
  }, [categoryName, textColor, bgColor, selectedIcon]);

  return (
    <>
      <Modal
        onEscape={() => closeModal()}
        className={clsx(["md:!max-w-[530px]", "md:!w-full"])}
      >
        <Modal.Header onClose={() => closeModal()} title="Editar categoría" />
        <Modal.Body>
          <div
            className={clsx([
              "flex",
              "w-full",
              "flex-col",
              "gap-4",
              "md:gap-6",
            ])}
          >
            <div className={clsx(["w-full", "rounded"])}>
              <div
                className={clsx([
                  "flex",
                  "w-full",
                  "flex-col",
                  "items-center",
                  "justify-center",
                  "p-4",
                  "md:p-6",
                  "cursor-pointer",
                  "rounded",
                  "transition-all",
                  "gap-1",
                  "shadow",
                  "!max-h-[140px]",
                  "!min-h-[140px]",
                  "md:!max-h-[160px]",
                  "md:!min-h-[160px]",
                  "h-full",
                ])}
                style={{
                  backgroundColor: rgbaObjectToString(bgColor),
                  color: rgbaObjectToString(textColor),
                }}
              >
                {!selectedIcon ? (
                  <>
                    <MdCategory size={ICON_SIZES.XL} />
                  </>
                ) : (
                  <Icon name={selectedIcon as any} size={ICON_SIZES.XL * 1.3} />
                )}
                <div
                  className={clsx([
                    "text-center",
                    "font-medium",
                    "md:text-sm",
                    "text-xs",
                    "max-w-full",
                    "truncate",
                  ])}
                >
                  {name}
                </div>
              </div>
            </div>
            <div className={clsx(["flex", "flex-col", "gap-4", "w-full"])}>
              <TextInput
                placeholder={t("pages:category.category_placeholder")}
                onChange={(e: any) => setCategoryName(e?.target?.value)}
                value={categoryName}
                label={t("pages:category.category_label")}
                required
              />
            </div>
            <div
              className={clsx([
                "grid",
                "grid-cols-1",
                "md:grid-cols-2",
                "gap-2",
              ])}
            >
              <ColorPicker
                textColor={textColor}
                onChange={(color: any) => {
                  setBgColor(color.rgb);
                }}
                color={bgColor}
                label={t("pages:category.collapsibles.colors.bg_color_label")}
                showText={false}
              />
              <ColorPicker
                showText={false}
                textColor={bgColor}
                onChange={(color: any) => {
                  setTextColor(color.rgb);
                }}
                color={textColor}
                label={t("pages:category.collapsibles.colors.text_color_label")}
              />
            </div>
            <IconSelector
              onClick={(icon) => setSelectedIcon(icon)}
              background={rgbaObjectToString(bgColor)}
              selected={selectedIcon}
              color={rgbaObjectToString(textColor) || ""}
            />
          </div>
        </Modal.Body>
        <Modal.Footer
          leftContent={hasChanges ? <ModalHasUnsavedChanges /> : null}
          actions={[
            {
              text: t("common:buttons.update"),
              onClick: async () => {
                if (ASK_CONFIRMATION_ON_SAVE_OR_UPDATE) {
                  setShowUpdateDialog(true);
                } else {
                  await updateCategory();
                }
              },
              icon: <MdCheck size={ICON_SIZES.SM} />,
              kind: EButtonKind.EDIT,
              disabled: hasFieldsComplete ? !hasChanges || isUpdating : true,
              isLoading: isUpdating,
            },
            {
              text: t("common:buttons.close"),
              onClick: () => closeModal(),
              kind: EButtonKind.NORMAL,
              icon: <MdClose size={ICON_SIZES.SM} />,
              disabled: isUpdating,
            },
          ]}
        />
      </Modal>
      {showUpdateDialog && (
        <ConfirmDialog
          title={t("dialogs:titles.update")}
          description={t("misc:strings.save_changes")}
          onClose={() => {
            setShowUpdateDialog(false);
          }}
          isLoading={isUpdating}
          confirmKind={EButtonKind.SAVE}
          onConfirm={async () => {
            await updateCategory();
          }}
        />
      )}
    </>
  );
};

export default memo(EditCategoryModal);
